import * as React from "react";
import {useEffect, useState} from "react";
import {translate} from "../../../translate/translate";
import {
    Alert,
    Box, Checkbox, Chip,
    CircularProgress, Fab,
    FormControl,
    Grid,
    InputLabel, ListItemText,
    MenuItem,
    Paper, Stack, TextField, Tooltip,
    Typography
} from "@mui/material";
import TablePaginationComponent from "../../../components/table/table-pagination.component";
import {Add, Edit, ErrorOutline, Search, WarningOutlined} from "@mui/icons-material";
import {URLS} from "../../../services/app-urls";
import {PaginatedResult} from "../../../models/default/paginated-result";
import {ErrorComponent} from "../../../components/error/error.component";
import {Client} from "../../../models/basic/client";
import {sortListObj, sortListString} from "../../../utils/list-utils";
import {Field, Form, Formik} from "formik";
import Select from "@mui/material/Select";
import TableActionComponent from "../../../components/table/table-action.component";
import CollapsibleTableComponent from "../../../components/table/collapsible-table.component";
import {AbstractService} from "../../../services/abstract-service";
import {useFilters} from "../../../filters/base-filter";
import {usePagination} from "../../../utils/pagination";
import AddPartNumberComponent from "./add-part-number.component";
import {FtBomFilter, FtBomService} from "../../../services/ft-bom-service";
import {GroupedFtBom} from "../../../models/basic/ft-bom";
import {toast} from "react-toastify";
import IconButton from "@mui/material/IconButton";
import GenericDialog from "../../../components/dialog/generic-dialog.component";
import {NavLink} from "react-router-dom";
import {useAuth} from "../../../contexts/auth-context";
import {Profile} from "../../../models/account/user";
import Button from "@mui/material/Button";
import AddFtbClientComponent from "./add-ftb-client.component";
import {Text} from "echarts/types/src/util/graphic";
import dayjs from "dayjs";


const FTBReportComponent = () => {

    const { user } = useAuth();
    const hasPermission = user?.perfil === Profile.ADMIN || user?.perfil === Profile.OPERACIONAL;

    const service = new FtBomService();
  
    const serviceClient = new AbstractService<Client>(URLS.CLIENT);
    const [dataSourceClient, setDataSourceClient] = React.useState<Client[]>([]);
  
    const [dataSourceFtBoms, setDataSourceFtBoms] = React.useState<string[]>();
    const [selectedFtBoms, setSelectedFtBoms] = useState<any[]>([]);
  
    const [ftBomsGroupedList, setFtBomsGroupedList] = React.useState<PaginatedResult<GroupedFtBom>>();
    
    const {filters, updateFilterKeepingParam, updateFilter } = useFilters<FtBomFilter>(
        { client: "", ft_bom: "", search: "", pendentes: "" },
        ["client", "ft_bom", "search", "pendentes"]
    );

    const [openedDetail, setOpenedDetail] = React.useState<{ [id: string]: boolean }>({});
    const [selectedRow, setSelectedRow] = React.useState<any | null>(null);

    const [loadingDetail, setLoadingDetail] = React.useState<{ [id: string]: boolean }>({});
    const [listDetail, setListDetail] = React.useState<{ [id: string]: any[] }>({});
    const [listDetailAlertGroup, setListDetailAlertGroup] = React.useState<{ [id: string]: boolean }>({});
    const [listDetailAlertPriority, setListDetailAlertPriority] = React.useState<{ [id: string]: boolean }>({});
    const [listDetailAlertUsage, setListDetailAlertUsage] = React.useState<{ [id: string]: boolean }>({});
    const [isEdit, setIsEdit] = React.useState(false);
    const [isReuse, setIsReuse] = React.useState(false);

    const {
        dataSource,
        pagination,
        resetPage,
        handleChangePage,
        handleChangeRowsPerPage
    } = usePagination<GroupedFtBom>(ftBomsGroupedList);

    const expand = ["*"];

    const [loading, setLoading] = React.useState(false);
    const [loadingInactivate, setLoadingInactivate] = React.useState(false);

    const [openDialog, setOpenDialog] = React.useState(false);
    const [openDialogInactivate, setOpenDialogInactivate] = React.useState(false);
	const [openClientDialog, setOpenClientDialog] = React.useState(false);
    const [lastUpdated, setLastUpdated] = React.useState<string>('');

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

    const handleClickAnchor = (event: React.MouseEvent<HTMLElement>, index: number) => {
        setAnchorEl(event.currentTarget);
        setSelectedIndex(index);
    };

    const handleCloseAnchor = () => {
        setAnchorEl(null);
        setSelectedIndex(null);
    };

    useEffect(() => {
        loadLastUpdated();
        loadClients();
        loadFtBoms();
        initializeFtBoms();
        search();
    }, []);
  
    useEffect(() => {
        if (dataSourceFtBoms?.length && !filters.ft_bom) {
            const all = dataSourceFtBoms.map((year: any) => year);
            setSelectedFtBoms(all || []);
            updateFilterKeepingParam("ft_bom", all.join(","));
        }
    }, [dataSourceFtBoms]);
    
    const initializeFtBoms = () => {
        if (!filters.ft_bom) {
            return;
        }
        const ftboms = filters.ft_bom?.split(",");
        setSelectedFtBoms(ftboms || []);
    }

    const loadLastUpdated = () => {
        service.get_last_updated_sap().then(response => {

            if (!response.data.dt_ultima_atualizacao || response.data.dt_ultima_atualizacao === '-') return "-";

            const dateStr = response.data.dt_ultima_atualizacao.endsWith('Z')
                ? response.data.dt_ultima_atualizacao
                : response.data.dt_ultima_atualizacao + 'Z';

            const date = new Date(dateStr);

            setLastUpdated(date.toLocaleString([], {
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                dateStyle: 'short',
                timeStyle: 'medium'
            }));
        }).catch((error: any) => {
            setLastUpdated(error.response.data.detail)
        });
    }

    const loadClients = () => {
        serviceClient.readAll({"active": true}).then(response => {
            const sortedData = sortListObj(response.data, "name");
            setDataSourceClient(sortedData);
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }
  
    const loadFtBoms = () => {
        service.listFtBomsSearch().then(response => {
            setDataSourceFtBoms(sortListString(response.data.ftboms));
        }).catch((error: any) => {
            ErrorComponent(error);
        });
    }

    const search = (restartIndex = false) => {
        setLoading(true);
        service.readPaginatedFtBomsGrouped(filters, expand, pagination).then(response => {
            setFtBomsGroupedList(response.data);
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoading(false);
        })

        restartIndex && resetPage();
        restartIndex && closeAllCollapses();
    }

    useEffect(() => {
        search();
    }, [pagination]);

    const handleOpenDialog = (row: any, edit = false) => {
        setSelectedRow(row);
        setOpenDialog(true);
        setIsEdit(edit);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedRow(null);
        setIsEdit(false);
        search();
    };

    const handleCloseDialogInactivate = () => {
      setOpenDialogInactivate(false);
    };

    const loadDetail = (row: any) => {
        setOpenedDetail({...openedDetail, [row.ft_bom]: !openedDetail[row.ft_bom]});

        if (!openedDetail[row.ft_bom]) {
            setListDetail({...listDetail, [row.ft_bom]: []});
            setListDetailAlertGroup({...listDetailAlertGroup, [row.ft_bom]: false});
            setListDetailAlertPriority({...listDetailAlertPriority, [row.ft_bom]: false});
            setListDetailAlertUsage({...listDetailAlertUsage, [row.ft_bom]: false});
            setLoadingDetail({...loadingDetail, [row.ft_bom]: true});
            const filter = { client_id: Number(row.client_id), ft_bom: row.ft_bom.toString() };
            service.listFtBomsChildren(filter).then(response => {
                setListDetail({...listDetail, [row.ft_bom]: response.data});
                setListDetailAlertGroup({...listDetailAlertGroup, [row.ft_bom]: row.alert_group});
                setListDetailAlertPriority({...listDetailAlertPriority, [row.ft_bom]: row.alert_priority});
                setListDetailAlertUsage({...listDetailAlertUsage, [row.ft_bom]: row.alert_usage});
            }).catch((error: any) => {
                ErrorComponent(error);
            }).finally(() => {
                setLoadingDetail({...loadingDetail, [row.ft_bom]: false});
            });
        }
    }

    const layoutDetail = (row: any) => {
        if (loadingDetail[row.ft_bom]) {
            return <Box padding={1}>
                <CircularProgress size="30px" />
            </Box>
        }
        return (
            <TablePaginationComponent
                key={row.ft_bom}
                state={listDetail[row.ft_bom] || []}
                displayedColumnsShow={["Alt Grp BoM", "Alt Grp Grl", "Foxconn PN", "Description EN", "Description PT", "Usage", "Priority"]}
                displayedColumns={["alternative_group_bom", "group", "foxconn_part_number", "description_english", "description_portuguese", "usage", "priority"]}
                numberColumns={["po_total", "project_total"]}
                currencyColumns={["po_total", "project_total"]}
                columnStyles={{1:"11%", 2:"11%", 3:"11%", 4:"14%", 5: "15%", 6:"15%", 7:"11%", 8:"11%"}}
                dateColumns={["purchase_date"]}
                customColumns={["priority"]}
                customCellRender={customCellRender}
                groupByColumn={"alternative_group_bom"}
                customHead={["alternative_group_bom", "priority", "usage"]}
                customHeadRender={customHeadRender}
            />
        )
    }
  
    const allFtBomsSelected = () => {
        return selectedFtBoms.length === dataSourceFtBoms?.length;
    }
  
    const renderFtBoms = (selected: any) => {
        if (allFtBomsSelected()) {
            return translate.t('all');
        }
        const sorted = sortListString(selected);
        return sorted.join(", ");
    };
    
    const openDetail = (row: any) => {
        setOpenedDetail({...openedDetail, [row.ft_bom]: !openedDetail[row.ft_bom]});
        loadDetail(row);
    }

    const closeAllCollapses = () => {
        setOpenedDetail({});
    }

    const changeStatus = (row: any) => {
        (row['status'] as boolean) = !row['status'];
        const payload = {
            status: row.status,
            client_id: Number(row.client_id),
            ft_bom: row.ft_bom.toString(),
            reuse: row.status ? false : isReuse,
        };
        setLoadingInactivate(true);
        service.changeStatus(payload).then(() => {
            toast.success(translate.t("successfully_updated"));
        }).catch((error: any) => {
            ErrorComponent(error);
        }).finally(() => {
            setLoadingInactivate(false);
            setOpenDialogInactivate(false);
            search();
        })
    }

    const handleSwitch = (row: any, column: string) => {
        setSelectedRow(row);
        setIsReuse(false);
        if (row['status']) {
            setOpenDialogInactivate(true);
        } else {
            changeStatus(row);
        }
    };

    const customHeadRender = (column: string, index: number, list: any[]) => {
        if (column === "alternative_group_bom") {
            return (
                <Stack direction={"row"}>
                    <Typography sx={{fontWeight: "bold"}}>
                        Alt Grp BoM
                    </Typography>
                    {listDetailAlertGroup[list[0].ft_bom] && (
                        <Tooltip title="Existem materiais sem 'Alt Grp BoM' definido" placement="top">
                            <WarningOutlined sx={{color: 'orange', verticalAlign: 'middle', display: 'inline'}}/>
                        </Tooltip>
                    )}
                </Stack>
            );
        }
        if (column === "priority") {
            return (
                <Stack direction={"row"}>
                    <Typography sx={{fontWeight: "bold"}}>
                        Priority
                    </Typography>
                    {listDetailAlertPriority[list[0].ft_bom] && (
                        <Tooltip title="Existem grupos sem a prioridade 'Primary' definida" placement="top">
                            <WarningOutlined sx={{color: 'orange', verticalAlign: 'middle', display: 'inline'}}/>
                        </Tooltip>
                    )}
                </Stack>
            );
        }
        if (column === "usage") {
            return (
                <Stack direction={"row"}>
                    <Typography sx={{fontWeight: "bold"}}>
                        Usage
                    </Typography>
                    {listDetailAlertUsage[list[0].ft_bom] && (
                        <Tooltip title="Existem grupos sem 'Usage' definido" placement="top">
                            <WarningOutlined sx={{color: 'orange', verticalAlign: 'middle', display: 'inline'}}/>
                        </Tooltip>
                    )}
                </Stack>
            );
        }
        return "-";
    };

    const customCellRender = (column: string, index: number, row: any) => {
        if (column === "priority") {
            return row["priority"] == 0 ? "Alternative" : "Primary";
        }

        if (column === "part_numbers") {
            if (row.part_numbers?.length && Array.isArray(row.part_numbers)) {

                const partNumbers = row.part_numbers;
                return (
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            "&:hover .hover-icon": {
                                opacity: 1
                            }
                        }}
                    >
                        <Grid>
                            {sortListObj(partNumbers, "part_number").map((part_number, index) => (
                                <Chip key={part_number.id} size="small" style={{ marginRight: '5px', marginBottom: '5px' }} label={part_number.part_number}/>
                            ))}
                        </Grid>
                        <IconButton
                            className="hover-icon"
                            onClick={() => handleOpenDialog(row, true)}
                            sx={{
                                opacity: 0,
                                transition: 'opacity 0.3s'
                            }}
                        >
                            <Edit />
                        </IconButton>
                    </Box>
                )
            }
            return (
                <Box display="flex" alignItems="center" flexDirection="row">
                </Box>
            );
        }

        if (column === "status_mount") {
            return <Chip size="small"
                        color={row.status_mount ? "success" : "warning"}
                        label={row.status_mount ? "Concluída" : "Pendente"}
                    />
        }

	    if (column.toLocaleLowerCase() === "client_name") {
		    if (row.client_name) {
			    return (
				    <Box
					    display="inline"
					    flexDirection="row"
					    justifyContent="space-between"
					    alignItems="center"
					    sx={{
						    "&:hover .hover-icon": {
							    opacity: 1
						    }
					    }}
				    >
					    { row.client_name }
					    <IconButton
						    className="hover-icon"
						    onClick={() => handleOpenClientEdit(row)}
						    sx={{
							    opacity: 0,
							    transition: 'opacity 0.3s'
						    }}
					    >
						    <Edit />
					    </IconButton>
				    </Box>
			    )
		    }
		    return  <Button
			    onClick={() => {
				    setSelectedRow(row);
				    setOpenClientDialog(true);
			    }}
			    color="secondary"
			    size="small">
			    <Add style={{fontSize: 'small'}}/>
			    {translate.t("classify")}
		    </Button>
	    }
        if (column === "ft_bom") {
            return (
                <Stack direction={"row"}>
                    <Typography>
                        { row.ft_bom }
                    </Typography>
                    {(row.alert_group || row.alert_priority || row.alert_usage) && (
                        <Tooltip title="Existem materiais com pendências" placement="top">
                            <WarningOutlined sx={{color: 'orange', verticalAlign: 'middle', display: 'inline'}}/>
                        </Tooltip>
                    )}
                </Stack>
            );
        }

        return row[column];
    }

    const customMenuItems = (row: any) => {
        return (
            <>
                <MenuItem disabled={!hasPermission}
                    onClick={()  => {
                        handleCloseAnchor();
                        row.part_numbers?.length > 0 ? handleOpenDialog(row, true) : handleOpenDialog(row)}}>{translate.t('add_partnumber')
                    }
                </MenuItem>
                <MenuItem // Redireciona para a página de montagem de Jiga e RMA
                    disabled={row.alert_group || row.alert_usage}
					component={NavLink}
					to={"/reports/ftb-general/mount-jiga-rma/client/" + (row.client_id ? row.client_id : 0) + "/ft-bom/" + row.ft_bom}
					onClick={handleCloseAnchor}>
                    {translate.t('mount_jiga_rma')}
                </MenuItem></>
        )
    }

	const handleOpenClientEdit = (row: GroupedFtBom) => {
		setSelectedRow(row);
		setOpenClientDialog(true);
	}

    return (
        <section>
            <div className="floating-title" style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                <Typography variant={"h5"}>
                    {translate.t("report_ftb_general")}
                </Typography>
            </div>

            <div className="floating-button" style={{display: 'flex', alignItems: 'center', gap: 1}}>
                {lastUpdated.toLowerCase().indexOf('erro') != -1 ? (
                    <span
                        className="text"
                        style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: 'red',
                            borderRadius: '50%',
                            display: 'inline-block',
                            marginRight: '5px'
                        }}
                    />
                ) : (
                    <span
                        className="text"
                        style={{
                            width: '10px',
                            height: '10px',
                            backgroundColor: 'green',
                            borderRadius: '50%',
                            display: 'inline-block',
                            marginRight: '5px'
                        }}
                    />
                )}
                <p className="text">{translate.t("last_updated")}: {lastUpdated}</p>
            </div>

            <Grid size={12}>
                <Paper variant="outlined">
                    <Box p={2}>
                        <Formik
                            enableReinitialize={true}
                            validateOnMount={true}
                            initialValues={{name: ""}}
                            onSubmit={(values, {setSubmitting}) => {
                                setTimeout(() => {
                                    search(true);
                                    setSubmitting(false);
                                }, 500);
                            }}
                        >
                            {
                                () => (
                                    <Form autoComplete="off">
                                        <Box alignItems="center" display="flex" justifyContent="space-between">
                                            <Grid alignItems="center" container spacing={2} direction={"row"}>
                                                <Grid size={2}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>
                                                            {translate.t("client")}
                                                        </InputLabel>
                                                        <Select
                                                            name="client"
                                                            label={translate.t("client")}
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={filters.client || "0"}
                                                            onChange={(event) => {
                                                                updateFilterKeepingParam("client", event.target.value as string)
                                                            }}
                                                        >
                                                            <MenuItem key="0" value="0">
                                                                {translate.t('all')}
                                                            </MenuItem>
                                                            {dataSourceClient?.map((client: Client) => (
                                                                <MenuItem key={client.id} value={client.id}>
                                                                    {client.name}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={2}>
                                                    <FormControl fullWidth>
                                                        <InputLabel>FT BOM</InputLabel>
                                                        <Select
                                                            name="ftbom"
                                                            label="FT BOM"
                                                            variant={"outlined"}
                                                            fullWidth
                                                            autoFocus={false}
                                                            value={selectedFtBoms}
                                                            onChange={(event: any) => {
                                                                if (event.target.value.includes(0)) {
                                                                    if (!allFtBomsSelected()) {
                                                                        const ftboms = dataSourceFtBoms?.map((ftbom: any) => ftbom);
                                                                        setSelectedFtBoms(ftboms || []);
                                                                        ftboms?.length && updateFilterKeepingParam("ft_bom", ftboms.join(","));
                                                                        return;
                                                                    }
                                                                    setSelectedFtBoms([]);
                                                                    updateFilterKeepingParam("ft_bom", "");
                                                                    return;
                                                                }
                                                                setSelectedFtBoms(event.target.value);
                                                                updateFilterKeepingParam("ft_bom", event.target.value.join(","));
                                                            }}
                                                            multiple
                                                            renderValue={(selected) => renderFtBoms(selected)}
                                                            MenuProps={{
                                                                PaperProps: {
                                                                    style: {
                                                                        maxHeight: '400px',
                                                                    },
                                                                },
                                                                disableAutoFocusItem: true,
                                                            }}
                                                            inputProps={{
                                                                sx: {
                                                                    maxWidth: '480px !important',
                                                                    width: '480px !important',
                                                                }
                                                            }}
                                                        >
                                                            <MenuItem key={0} value={0}>
                                                                <Checkbox checked={allFtBomsSelected()}/>
                                                                <ListItemText primary={
                                                                    <Box display="flex" alignItems="center"
                                                                         justifyContent="space-between">
                                                                        <Typography
                                                                            variant="body1">{translate.t('all')}</Typography>
                                                                        {allFtBomsSelected() &&
                                                                            <Typography variant="body2"
                                                                                        color="gray">{translate.t('clear')}</Typography>}
                                                                    </Box>
                                                                }/>
                                                            </MenuItem>
                                                            {dataSourceFtBoms?.map((ftbom: any) => (
                                                                <MenuItem key={ftbom} value={ftbom}>
                                                                    <Checkbox checked={selectedFtBoms.includes(ftbom)}/>
                                                                    <ListItemText primary={ftbom}/>
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                                <Grid size={4}>
                                                    <Field
                                                        as={TextField}
                                                        name="search"
                                                        label={""}
                                                        fullWidth
                                                        variant={"outlined"}
                                                        autoFocus={true}
                                                        placeholder={translate.t("search_material_by_code_partnumber_description")}
                                                        value={filters.search}
                                                        onChange={(event: React.ChangeEvent<{
                                                            value: unknown
                                                        }>) => updateFilterKeepingParam("search", event.target.value)}
                                                    />
                                                </Grid>
                                                <Grid size={3}>
                                                    <Checkbox
                                                        checked={filters.pendentes === "true"}
                                                        onChange={(event) => {
                                                            updateFilterKeepingParam("pendentes", event.target.checked ? "true" : "false");
                                                        }}/>
                                                    Montagem JIGA/RMA pendente
                                                </Grid>
                                                <Grid size={1}>
                                                    <Fab
                                                        size="small"
                                                        type="submit"
                                                        color="secondary">
                                                        <Search/>
                                                    </Fab>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Form>
                                )}
                        </Formik>
                    </Box>
                </Paper>
            </Grid>

            <Paper variant="outlined" sx={{marginTop: "16px"}}>
                <CollapsibleTableComponent
                    state={dataSource}
                    displayedColumnsShow={["Client", "FT BOM", "Total Produced", "Quantity of Jiga", "Part Numbers", "JIGA/RMA", "Status", "Action"]}
                    displayedColumns={["client_name", "ft_bom", "total_produced", "total_jiga", "part_numbers", "status_mount", "status", "action"]}
                    numberColumns={["total_produced", "total_jiga"]}
                    integerColumns={["total_produced", "total_jiga"]}
                    highlightedColumns={["client_name", "ft_bom", "total_produced", "total_jiga", "part_numbers", "status_mount", "status", "action"]}
                    highlightedColor={"#E5ECFF"}
                    highlightedHeader={false}
                    columnStyles={{1: "15%", 2: "25%", 3: "10%", 4: "10%", 5: "25%", 6: "10%", 7: "5%"}}
                    actionsComponent={TableActionComponent}
                    childrenComponent={layoutDetail}
                    count={ftBomsGroupedList?.count}
                    next={ftBomsGroupedList?.next}
                    previous={ftBomsGroupedList?.previous}
                    page={pagination.page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pagination.limit}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 50, 100]}
                    loading={loading}
                    loadOnOpen={openDetail}
                    openControl={openedDetail}
                    openKey={"ft_bom"}
                    handleSwitch={handleSwitch}
                    disabledSwitch={!hasPermission}
                    customCellRender={customCellRender}
                    customColumns={["client_name", "ft_bom", "part_numbers", "submenu", "status_mount"]}
                    customMenuItems={customMenuItems}
                    menuLayout={true}
                    showActions={["custom"]}
                />
            </Paper>

            <AddPartNumberComponent openDialog={openDialog} handleCloseDialog={handleCloseDialog} row={selectedRow}
                                    isEdit={isEdit}/>

            <AddFtbClientComponent
                openDialog={openClientDialog}
                ft_bom={selectedRow?.ft_bom}
                client_id={selectedRow?.client_id}
                handleCloseDialog={(save: boolean) => {
                    if (save) {
                        search();
                    }
                    setSelectedRow(undefined);
                    setOpenClientDialog(false);
                }}
            />

            <GenericDialog
                open={openDialogInactivate}
                onClose={handleCloseDialogInactivate}
                title={"Inativar a " + (selectedRow ? selectedRow.ft_bom.toString() : '')}
                colorButton={"primary"}
                labelButton={"confirm"}
                handleConfirm={() => changeStatus(selectedRow)}
                loading={loadingInactivate}>
                <Box>
                    {(selectedRow && !selectedRow.status_mount) && (
                        <div>
                            <Typography style={{fontWeight: 'bold'}}>
                                {"Deseja inativar esta  " + (selectedRow ? selectedRow.ft_bom.toString() : '') + '?'}
                            </Typography>
                        </div>
                    )}
                    {(selectedRow && selectedRow.status_mount) && (
                        <div>
                            <Typography style={{fontWeight: 'bold'}}>
                                {"Deseja habilitar esta FTB para reutilização de materiais?"}
                            </Typography>
                        </div>
                    )}
                    {(selectedRow && selectedRow.status_mount) && (
                        <div style={{marginTop: "20px"}}>
                            <Checkbox checked={isReuse}
                                      onChange={(event) => setIsReuse(event.target.checked)}
                            /> {"Habilitar para reutilização"}
                        </div>
                    )}
                </Box>
            </GenericDialog>
        </section>
    );
};

export default FTBReportComponent;
